import ErrorBoundary from 'containers/error_boundary'
import EmojiSelector from 'controls/dropdowns/emoji_selector'
import NimbleEmoji from 'emoji-mart/dist-es/components/emoji/nimble-emoji'
import { useFetchEmojiData } from 'hooks/use_fetch_emoji_data'
import { useMediaQuery } from 'hooks/use_media_query'
import { EmojiIcon } from 'images'
import PropTypes from 'prop-types'
import React, { useContext, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getSelectedTopicId } from 'reduxes/slices/selected/selectors'
import { toggleEmojiReaction } from 'reduxes/slices/timelines/actions'

import { MEDIA_QUERIES, css } from 'design'
import * as Popover from 'primitives/popover'
import { Tooltip } from 'primitives/tooltip'
import { cn } from 'util/cn'

import { makeGetNamesForHandles } from '../../../reduxes/slices/contacts/selectors'
import { getCurrentParticipantForSelectedTopic } from '../../../reduxes/slices/current/selectors'
import { TimelinesFocusContext } from '../timelines_focus_manager'
import {useAxios} from "../../../hooks/use_axios";

const generateEmojiDOMId = (emojiName, timelineId) => {
  let hash = 0,
    i,
    chr
  for (i = 0; i < emojiName.length; i++) {
    chr = emojiName.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0
  }
  return 'timeline-' + timelineId + '-emoji' + hash
}

export default function EmojiReactions({ timeline }) {
  const dispatch = useDispatch()
  const axios = useAxios()
  const topicId = useSelector(getSelectedTopicId)
  const currentParticipant = useSelector(getCurrentParticipantForSelectedTopic)
  const popoverContentRef = useRef(null)

  const { setFocusLock } = useContext(TimelinesFocusContext)

  const { data } = useFetchEmojiData()

  const handlesForAllReactions = useMemo(() => {
    return Object.keys(timeline.emoji_reactions).reduce((result, emojiName) => {
      const handles = timeline.emoji_reactions[emojiName]?.handles || []
      return [...result, ...handles]
    }, [])
  }, [timeline])
  const getNamesForHandles = useMemo(makeGetNamesForHandles, [])
  const namesLookup = useSelector((state) =>
    getNamesForHandles(state, handlesForAllReactions),
  )

  const emojis = useMemo(() => {
    const joinNames = (handles) => {
      const names = handles.map((handle) => namesLookup[handle])
      if (names.length > 1) {
        return names.slice(0, -1).join(', ') + ' and ' + names.slice(-1)
      } else {
        return names
      }
    }

    const toggleEmojiAdded = (emojiName) => {
      if (!topicId || !timeline || !emojiName) return

      dispatch(axios(toggleEmojiReaction(topicId, timeline.id, emojiName)))
    }

    const emojiReactions = timeline.emoji_reactions

    return Object.keys(emojiReactions)
      .sort(
        (a, b) =>
          Date.parse(emojiReactions[a].timestamp) -
          Date.parse(emojiReactions[b].timestamp),
      )
      .reduce((result, emojiName) => {
        const id = generateEmojiDOMId(emojiName, timeline.id)
        const handles = emojiReactions[emojiName].handles
        const noOfReactions = handles.length
        const names = joinNames(handles)
        const hasReacted =
          currentParticipant &&
          emojiReactions[emojiName].handles.includes(currentParticipant.handle)

        result.push(
          <Tooltip
            tooltip={`${names} reacted with ${emojiName}`}
            side="top"
            key={id}
          >
            <div
              className={'emoji-reaction ' + (hasReacted ? ' has-reacted' : '')}
              onClick={toggleEmojiAdded.bind(this, emojiName)}
            >
              {data && (
                <NimbleEmoji
                  emoji={emojiName}
                  size={16}
                  set="apple"
                  native={true}
                  data={data}
                />
              )}

              <div className={'count'}>{noOfReactions}</div>

              <div className={'tooltip-zone'} id={id} />
            </div>
          </Tooltip>,
        )
        return result
      }, [])
  }, [currentParticipant, data, dispatch, namesLookup, timeline, topicId])

  const desktop = useMediaQuery(MEDIA_QUERIES.md)

  if (!timeline) {
    return null
  }

  const timelineId = timeline.id

  return emojis.length > 0 ? (
    <div className={`emoji-reaction-container`}>
      <ErrorBoundary>
        <>{emojis}</>
      </ErrorBoundary>
      {!!Object.keys(timeline.emoji_reactions).length && (
        <Tooltip tooltip="Add reaction">
          <div className={tooltipTriggerStyles()}>
            <Popover.Root
              onOpenChange={(open) => {
                if (desktop && setFocusLock) {
                  setFocusLock(open ? timelineId : undefined)
                }
              }}
            >
              {!desktop && <Popover.Anchor className={anchorStyles()} />}
              <Popover.Trigger
                className={cn('add-emoji-reaction-inline-btn')}
                title="Add reaction"
              >
                <EmojiIcon />
              </Popover.Trigger>
              <Popover.Content
                className={emojiSelectorResponsiveStyles()}
                side="top"
                ref={popoverContentRef}
              >
                <EmojiSelector timelineId={timelineId} />
              </Popover.Content>
            </Popover.Root>
          </div>
        </Tooltip>
      )}
    </div>
  ) : null
}

EmojiReactions.propTypes = {
  timeline: PropTypes.object,
  align: PropTypes.oneOf(['left', 'center', 'right']),
}

const anchorStyles = css({
  position: 'absolute',
  left: '$space$2',
})

const tooltipTriggerStyles = css({
  WebkitAppearance: 'none !important',
})

export const emojiSelectorResponsiveStyles = css({
  '@md': {
    left: 'initial',
  },
})
